import { toPng } from "html-to-image";
import api from "../api";

const ALLOWED_HOSTS = new Set([
  "hhcourses-assets.s3.us-east-2.amazonaws.com",
  "d10jmd9bhq0s4v.cloudfront.net",
]);

function isRemoteShareImage(src) {
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) return false;
  try {
    const url = new URL(src, window.location.href);
    return url.protocol === "https:" && ALLOWED_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function waitForImage(image) {
  if (image.complete) return Promise.resolve();
  return new Promise((resolve) => {
    image.onload = () => resolve();
    image.onerror = () => resolve();
  });
}

async function decodeImages(node) {
  const images = Array.from(node.querySelectorAll("img"));
  await Promise.all(
    images.map(async (image) => {
      await waitForImage(image);
      if (typeof image.decode === "function") {
        await image.decode().catch(() => {});
      }
    })
  );
}

/**
 * html-to-image cannot read CloudFront or S3 pixels. Fetch those through the
 * API and swap them for data URLs before capture.
 */
export async function inlineRemotePosterImages(node) {
  if (!node) return;
  const images = Array.from(node.querySelectorAll("img"));
  await Promise.all(
    images.map(async (image) => {
      const src = image.currentSrc || image.getAttribute("src") || image.src;
      if (!isRemoteShareImage(src)) return;
      try {
        const response = await api.get("/api/share-image", {
          params: { url: src },
          responseType: "blob",
        });
        const dataUrl = await blobToDataUrl(response.data);
        image.removeAttribute("crossorigin");
        image.src = dataUrl;
        await waitForImage(image);
      } catch {
        // Leave the original src. Capture still proceeds for the rest of the poster.
      }
    })
  );
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load poster capture"));
    image.src = src;
  });
}

function isTransparent(color) {
  return !color || color === "transparent" || /rgba\([^)]*,\s*0\)$/.test(color);
}

function parseLength(value, basis) {
  const number = parseFloat(value) || 0;
  return String(value).trim().endsWith("%") ? (number / 100) * basis : number;
}

// Computed box-shadow lists the color first, e.g. "rgba(0, 0, 0, 0.3) 8px 14px 24px 0px".
function parseShadows(boxShadow) {
  if (!boxShadow || boxShadow === "none") return [];
  return boxShadow
    .split(/,(?![^(]*\))/)
    .map((raw) => {
      const text = raw.trim();
      const match = text.match(
        /^(rgba?\([^)]*\)|#[0-9a-f]+|[a-z]+)\s+(-?[\d.]+)px\s+(-?[\d.]+)px(?:\s+([\d.]+)px)?/i
      );
      if (!match) return null;
      return {
        text,
        inset: /\binset\b/.test(text),
        color: match[1],
        offsetX: parseFloat(match[2]) || 0,
        offsetY: parseFloat(match[3]) || 0,
        blur: parseFloat(match[4]) || 0,
      };
    })
    .filter(Boolean);
}

function parseTransform(transform) {
  const match = /^matrix\(([^)]+)\)$/.exec(transform || "");
  if (!match) return { angle: 0, scale: 1 };
  const [a, b] = match[1].split(",").map(Number);
  return { angle: Math.atan2(b, a), scale: Math.hypot(a, b) || 1 };
}

function measurePosterImage(image, nodeRect) {
  const style = window.getComputedStyle(image);
  if (style.display === "none" || style.visibility === "hidden") return null;
  if (!image.naturalWidth || !image.offsetWidth || !image.offsetHeight) return null;

  const rect = image.getBoundingClientRect();
  const { angle, scale } = parseTransform(style.transform);
  const width = image.offsetWidth * scale;
  const height = image.offsetHeight * scale;
  return {
    image,
    centerX: rect.left + rect.width / 2 - nodeRect.left,
    centerY: rect.top + rect.height / 2 - nodeRect.top,
    width,
    height,
    angle,
    radius: Math.min(
      parseLength(style.borderTopLeftRadius, image.offsetWidth) * scale,
      width / 2,
      height / 2
    ),
    fit: style.objectFit || "fill",
    background: isTransparent(style.backgroundColor) ? null : style.backgroundColor,
    shadow: parseShadows(style.boxShadow).find((layer) => !layer.inset) || null,
  };
}

// Outer shadows on untransformed elements; inset shadows stay in the SVG capture.
function measureOuterShadow(element, nodeRect) {
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return null;
  if (style.transform && style.transform !== "none") return null;
  const layers = parseShadows(style.boxShadow);
  const outer = layers.filter((layer) => !layer.inset);
  if (outer.length === 0) return null;

  const rect = element.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  return {
    element,
    x: rect.left - nodeRect.left,
    y: rect.top - nodeRect.top,
    width: rect.width,
    height: rect.height,
    radius: Math.min(
      parseLength(style.borderTopLeftRadius, rect.width),
      rect.width / 2,
      rect.height / 2
    ),
    // CSS paints the first shadow on top, so the canvas draws them in reverse.
    outer: outer.slice().reverse(),
    keptShadow: layers
      .filter((layer) => layer.inset)
      .map((layer) => layer.text)
      .join(", ") || "none",
  };
}

function addRoundedRect(ctx, x, y, width, height, radius) {
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, width, height, radius);
    return;
  }
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function fitBoxes(image, width, height, fit) {
  const iw = image.naturalWidth;
  const ih = image.naturalHeight;
  const full = { sx: 0, sy: 0, sw: iw, sh: ih };
  if (fit === "cover") {
    const ratio = Math.max(width / iw, height / ih);
    const sw = width / ratio;
    const sh = height / ratio;
    return {
      sx: (iw - sw) / 2,
      sy: (ih - sh) / 2,
      sw,
      sh,
      dx: -width / 2,
      dy: -height / 2,
      dw: width,
      dh: height,
    };
  }
  if (fit === "contain" || fit === "scale-down") {
    let ratio = Math.min(width / iw, height / ih);
    if (fit === "scale-down") ratio = Math.min(ratio, 1);
    const dw = iw * ratio;
    const dh = ih * ratio;
    return { ...full, dx: -dw / 2, dy: -dh / 2, dw, dh };
  }
  return { ...full, dx: -width / 2, dy: -height / 2, dw: width, dh: height };
}

function drawPosterImage(ctx, item, pixelScale) {
  const { image, width, height, radius, angle, shadow, background } = item;
  const x = -width / 2;
  const y = -height / 2;

  ctx.save();
  ctx.translate(item.centerX, item.centerY);
  ctx.rotate(angle);

  if (shadow) {
    // Canvas shadows ignore the transform, so scale and rotate them by hand.
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    ctx.save();
    ctx.shadowColor = shadow.color;
    ctx.shadowBlur = shadow.blur * pixelScale;
    ctx.shadowOffsetX = (shadow.offsetX * cos - shadow.offsetY * sin) * pixelScale;
    ctx.shadowOffsetY = (shadow.offsetX * sin + shadow.offsetY * cos) * pixelScale;
    ctx.fillStyle = background || "#ffffff";
    ctx.beginPath();
    addRoundedRect(ctx, x, y, width, height, radius);
    ctx.fill();
    ctx.restore();
  }

  ctx.beginPath();
  addRoundedRect(ctx, x, y, width, height, radius);
  ctx.clip();
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(x, y, width, height);
  }
  const box = fitBoxes(image, width, height, item.fit);
  ctx.drawImage(image, box.sx, box.sy, box.sw, box.sh, box.dx, box.dy, box.dw, box.dh);
  ctx.restore();
}

// Box shadows never paint under their own box, so draw only the part outside it.
function drawElementShadows(ctx, item, pixelScale, canvasWidth, canvasHeight) {
  // Paint the box off canvas and pull only its shadow back into view.
  const far = canvasWidth * 2;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, canvasWidth, canvasHeight);
  addRoundedRect(ctx, item.x, item.y, item.width, item.height, item.radius);
  ctx.clip("evenodd");
  item.outer.forEach((shadow) => {
    ctx.shadowColor = shadow.color;
    ctx.shadowBlur = shadow.blur * pixelScale;
    ctx.shadowOffsetX = (shadow.offsetX + far) * pixelScale;
    ctx.shadowOffsetY = shadow.offsetY * pixelScale;
    ctx.fillStyle = "#000000";
    ctx.beginPath();
    addRoundedRect(ctx, item.x - far, item.y, item.width, item.height, item.radius);
    ctx.fill();
  });
  ctx.restore();
}

/**
 * WebKit often leaves images inside html-to-image's SVG blank and misdraws
 * box shadows there. So the poster is captured with its images hidden and
 * outer shadows removed, and those are drawn onto a canvas afterwards.
 */
export async function capturePosterPng(node, { backgroundColor } = {}) {
  await inlineRemotePosterImages(node);
  await decodeImages(node);
  await new Promise((resolve) => setTimeout(resolve, 80));

  const options = { pixelRatio: 2, cacheBust: false, backgroundColor };
  const nodeRect = node.getBoundingClientRect();
  const items = Array.from(node.querySelectorAll("img"))
    .map((image) => measurePosterImage(image, nodeRect))
    .filter(Boolean);
  const shadows = [node, ...node.querySelectorAll("*")]
    .filter((element) => element.tagName !== "IMG")
    .map((element) => measureOuterShadow(element, nodeRect))
    .filter(Boolean);
  if (items.length === 0 && shadows.length === 0) return toPng(node, options);

  const previousVisibility = items.map((item) => item.image.style.visibility);
  const previousShadow = shadows.map((item) => item.element.style.boxShadow);
  items.forEach((item) => {
    item.image.style.visibility = "hidden";
  });
  shadows.forEach((item) => {
    item.element.style.boxShadow = item.keptShadow;
  });
  let basePng;
  try {
    basePng = await toPng(node, options);
  } finally {
    items.forEach((item, index) => {
      item.image.style.visibility = previousVisibility[index];
    });
    shadows.forEach((item, index) => {
      item.element.style.boxShadow = previousShadow[index];
    });
  }

  try {
    const base = await loadImage(basePng);
    const canvas = document.createElement("canvas");
    canvas.width = base.naturalWidth;
    canvas.height = base.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(base, 0, 0);

    const pixelScale = base.naturalWidth / (nodeRect.width || node.offsetWidth || 1);
    ctx.scale(pixelScale, pixelScale);
    const width = canvas.width / pixelScale;
    const height = canvas.height / pixelScale;
    shadows.forEach((item) => drawElementShadows(ctx, item, pixelScale, width, height));
    items.forEach((item) => drawPosterImage(ctx, item, pixelScale));
    return canvas.toDataURL("image/png");
  } catch {
    // A tainted or failed canvas still leaves a poster with its images inline.
    return toPng(node, options);
  }
}
